import type { IDynamicLinkedServiceTypeMetadata, ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';

import { format } from 'app/shared/utils/string.util';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { ConnectionAuthKind, Type } from 'shell-models-lib';
import { BuiltInDataSourceOptionId, DataSourceOptionKind } from '@powerquery/mashup-editor-client';
import { GlobalVariables } from 'app/services/globals';

const commonControls: ControlMetadata[] = [
    {
        type: 'textbox',
        bind: 'server',
        label: stringResources.ModelName_ServerName,
        required: true,
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        placeholder: stringResources.ModelName_ServerHint,
        description: stringResources.ModelName_ServerName_Description,
        isLabelVertical: true
    },
    {
        type: 'textbox',
        bind: 'database',
        label: stringResources.ModelName_DatabaseName,
        required: true,
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        isLabelVertical: true
    },
    {
        type: 'textbox',
        bind: 'packageCollection',
        label: stringResources.ModelName_PackageCollection,
        isLabelVertical: true
    },
    {
        type: 'textbox',
        bind: 'certificateCommonName',
        label: stringResources.ModelName_CertificateCommonName,
        isLabelVertical: true
    },
    {
        type: 'textbox',
        bind: 'username',
        label: stringResources.ModelName_UserName,
        required: true,
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        isLabelVertical: true
    }
];

const authenticationTypeControl: ControlMetadata = {
    type: 'select-switch',
    bind: 'authenticationType',
    label: stringResources.ModelName_AuthenticationType,
    options: [
        {
            label: stringResources.Basic_DisplayText,
            value: 'Basic'
        }
    ],
    isLabelVertical: true,
    defaultValue: 'Basic'
};

const dynamicPasswordControl: ControlMetadata = {
    enableAKVParameterization: true,
    hideAKVSwitcherInConnStrProps: true,
    type: 'dynamic-akv-switcher',
    bind: 'password',
    label: stringResources.ModelName_Password,
    child: {
        type: 'parameterizable',
        attribute: 'password',
        disableAddParameter: true,
        child: {
            type: 'password',
            label: stringResources.ModelName_Password,
            required: true,
            isLabelVertical: true,
            validators: [
                { type: 'required', errorMessage: '' }
            ],
        }
    },
    description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
        format(stringResources.ModelName_LinkedServiceTemplate, stringResources.Db2_DisplayText),
        stringResources.ModelName_Password)
};

const additionalFieldsControl: ControlMetadata = {
    type: 'key-val-list',
    bind: 'additionalFields',
    hide: '{ isOldMashupResource }',
    label: stringResources.AdditionalConnectionStringFields
};

const getDb2LinkedSrvControls = (): ControlMetadata[] => {
    const controls: ControlMetadata[] = commonControls.map((control) => ({ type: 'parameterizable', child: control }));
    controls.splice(4, 0, authenticationTypeControl);
    return [
        ...controls,
        dynamicPasswordControl,
        additionalFieldsControl
    ] as ControlMetadata[];
};

export const db2Metadata: IDynamicLinkedServiceTypeMetadata = async (injector) => {
    const metadata: ILinkedServiceTypeMetadata = {
        type: adfModelTypeMap.db2.linkedServiceType,
        displayName: stringResources.Db2_DisplayText,
        linkedServiceKind: 'table',
        icon: {
            className: 'svg-i-100x100-DB2'
        },
        distinguishTableView: true,
        category: ['Database'],
        supportDatasetV2: true,
        isFirstClass: true,
        supportedInTrident: true,
        pqoDataSourceOption: {
            kind: DataSourceOptionKind.BuiltIn,
            id: BuiltInDataSourceOptionId.Db2
        },
        supportOPDG: GlobalVariables.enableOPDG,
        supportedAuthKindsInTrident: [ConnectionAuthKind.Basic],
        supportCopyJobSource: true,
        supportMultipleIncrementalColumns: true,
        supportAutoFillCopilotInput: true,
        isKnownInPipelineContext: true,
        supportFirstClassParameterExperience: true,
        tableColumnNameQuotes: ['"', '"'],
        copySinkTypes: [],
        copySourceTypes: [adfModelTypeMap.db2.copySourceType],
        timedQueryTemplate: 'select * from <<tableName>> ' +
            '\nwhere <<columnName>> >= TO_DATE(\'@{formatDateTime(pipeline().parameters.windowStart, \'yyyy-MM-dd HH:mm\' )}\', \'YYYY-MM-DD HH24:MI\') ' +
            '\nAND <<columnName>> < TO_DATE(\'@{formatDateTime(pipeline().parameters.windowEnd, \'yyyy-MM-dd HH:mm\' )}\', \'YYYY-MM-DD HH24:MI\')',
        deltaLoadQueryTemplate: `select * from <<tableName>>
        where <<columnName>> > @{if(contains(json(item().DataLoadingBehaviorSettings).watermarkColumnType, 'Int'),
        json(item().DataLoadingBehaviorSettings).watermarkColumnStartValue,
        concat('TO_DATE(''', formatDateTime(json(item().DataLoadingBehaviorSettings).watermarkColumnStartValue, 'yyyy-MM-dd HH:mm'), ''', ''YYYY-MM-DD HH24:MI'')'))}
        and <<columnName>> <= @{if(contains(json(item().DataLoadingBehaviorSettings).watermarkColumnType, 'Int'),
        activity('GetMaxWatermarkValue').output.firstRow.CurrentMaxWaterMarkColumnValue,
        concat('TO_DATE(''', formatDateTime(activity('GetMaxWatermarkValue').output.firstRow.CurrentMaxWaterMarkColumnValue, 'yyyy-MM-dd HH:mm'), ''', ''YYYY-MM-DD HH24:MI'')'))}`,
        controls: [
            {
                type: 'select-button-switch',
                required: true,
                isLabelVertical: true,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                disabled: '{isOldMashupResource ? true : false}' as any,
                hideOptionsWhenDisabled: true,
                options: [
                    {
                        label: stringResources.ModelName_ConnectionString,
                        value: false
                    },
                    {
                        label: stringResources.AzureKeyVault_DisplayText,
                        value: true,
                        hideAKVOptionIfNotChosen: false
                    }
                ],
                bind: 'enableAKV',
                isNotRelatedToCredential: true,
                defaultValue: false,
                cases: [
                    {
                        caseValue: true,
                        children: [
                            {
                                type: 'azure-key-vault',
                                enableAKVParameterization: true,
                                bind: 'referedAKV',
                                isLabelVertical: true,
                                description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template_ConnectionStringWithSample,
                                    format(stringResources.ModelName_LinkedServiceTemplate, stringResources.Db2_DisplayText),
                                    'server=<server:port>;database=<database>;authenticationType=Basic;username=<username>;password=<password>;packageCollection=<packagecollection>;certificateCommonName=<certname>;')
                            }
                        ]
                    },
                    {
                        caseValue: false,
                        children: getDb2LinkedSrvControls()
                    }
                ]
            }
        ],
        additionalPropertiesInFabric: [
            {
                label: 'Package collection',
                value: 'packageCollection',
                type: Type.String
            }
        ]
    };
    return metadata;
};
